// #region Dependencias
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import styled from 'styled-components'
import { IconBack, IconCancel, IconConfirm, IconOpen, IconSave } from 'components/Icons'
import { Text } from 'components/Library'
import { colors } from 'components/Library/theme'
import MenuBorder from './Menus/MenuBorder'
import MenuCallToAction from './Menus/MenuCallToAction'
import MenuColor from './Menus/MenuColor'
import MenuDatos from './Menus/MenuDatos'
import MenuFont from './Menus/MenuFont'
import { OPCIONES } from 'utils/constants'
import MenuProducts from './Menus/MenuProducts'
import MenuBenefits from './Menus/MenuBenefits'
import { useTranslation } from 'react-i18next'
// #endregion

// #region Estilos
const StyledNavbar = styled.nav`
  align-items: center;
  background-color: ${colors.primary};
  color: ${colors.white};
  display: flex;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  user-select: none;
`

const StyledSpan = styled.span`
  display: flex;
`

const StyledAside = styled.aside`
  background-color: ${colors.light};
  bottom: 0;
  border-left: 1px solid ${colors.gray};
  box-shadow: 0 4px 4px rgba(0, 0, 0, 0.25);
  /* padding: 1rem; */
  /* position: fixed; */
  right: 0;
  top: 0;
  min-width: 250px;
  position: relative;
`

const StyledContainer = styled.div`
  position: sticky;
  top: 0;
  right: 0;
  /* left: 0; */
`

const StyledUl = styled.ul`
  /* background-color: red; */
  list-style: none;
  margin: 0;
  padding: 0;
`

const StyledLi = styled.li`
  background-color: ${colors.light};
  padding: 1rem;

  &:hover {
    /* background-color: blue; */
    cursor: pointer;
    filter: brightness(0.9);
  }
`
// #endregion

/**
 * Componente para mostrar la barra lateral de una página en modo de edición.
 * @class
 * @param {Object} props - Propiedades del componente.
 * @param {boolean} props.modoEdicion - Valor que indica si se está en modo de edición.
 * @param {Function} props.handleSave - Callback para guardar los cambios de la página.
 * @param {Object} props.formValues - Objeto con los valores de los campos del formulario de la página.
 * @param {Function} props.setFormValues - Callback para actualizar los valores del formulario de la página directamente.
 * @param {Function} props.handleInputChange - Callback para actualizar los valores de un elemento de la página.
 */
const Sidebar = ({
  modoEdicion = false,
  handleSave,
  formValues,
  setFormValues,
  handleInputChange
}) => {
  const navigate = useNavigate()
  const { i18n } = useTranslation()
  const { url } = useParams()
  const [opcion, setOpcion] = useState(0)

  /**
   * Función para abrir la página seleccionada actual en una nueva ventana
   * si está en modo edición.
   */
  const handleOpen = () => {
    if (!modoEdicion) return
    window.open(`/${url}`, '_blank')
  }

  /**
   * Función para cancelar la edición del contenido actual y volver
   * al menú principal de opciones.
   */
  const handleCancel = () => {
    setOpcion(0)
  }

  /**
   * Función para confirmar la edición del contenido actual y volver
   * al menú principal de opciones.
   */
  const handleConfirm = () => {
    setOpcion(0)
  }

  return (
    <StyledAside>
      <StyledContainer>
        <StyledNavbar>
          {opcion === 0 && <IconBack hover title="Regresar" onClick={() => navigate('/pages')} />}
          {opcion !== 0 && (
            <>
              <IconCancel hover title="Cancelar" onClick={handleCancel} />
              <Text weight="bold" align="center">
                {OPCIONES[opcion - 1] || ''}
              </Text>
            </>
          )}
          <StyledSpan>
            {opcion === 0 && (
              <>
                <IconOpen disabled={!modoEdicion} hover title="Abrir página" onClick={handleOpen} />
                <IconSave
                  hover
                  title="Guardar cambios"
                  onClick={handleSave}
                  style={{ marginLeft: '1rem' }}
                />
              </>
            )}
            {opcion !== 0 && <IconConfirm hover title="Aceptar" onClick={handleConfirm} />}
          </StyledSpan>
        </StyledNavbar>
        {opcion === 0 && (
          <StyledUl>
            {OPCIONES.map((opcion, index) => (
              <StyledLi key={index} onClick={() => setOpcion(index + 1)}>
                {opcion}
              </StyledLi>
            ))}
          </StyledUl>
        )}
        {opcion === 1 && (
          <MenuDatos
            idioma={i18n.language}
            formValues={formValues}
            handleInputChange={handleInputChange}
          />
        )}
        {opcion === 2 && (
          <MenuCallToAction
            idioma={i18n.language}
            tipo="main"
            formValues={formValues.secciones.main}
            handleInputChange={handleInputChange}
          />
        )}
        {opcion === 3 && (
          <MenuCallToAction
            idioma={i18n.language}
            tipo="footer"
            formValues={formValues.secciones.footer}
            handleInputChange={handleInputChange}
          />
        )}
        {opcion === 4 && (
          <MenuColor
            color={formValues.opciones.colores.primario}
            setValues={setFormValues}
            tipo="primario"
          />
        )}
        {opcion === 5 && (
          <MenuBorder borde={formValues.opciones.borde} handleInputChange={handleInputChange} />
        )}
        {opcion === 6 && <MenuFont formValues={formValues} handleInputChange={handleInputChange} />}
        {opcion === 7 && (
          <MenuProducts
            idioma={i18n.language}
            formValues={formValues}
            handleInputChange={handleInputChange}
          />
        )}
        {opcion === 8 && (
          <MenuBenefits
            idioma={i18n.language}
            tipo="beneficios"
            formValues={formValues.secciones.beneficios}
            handleInputChange={handleInputChange}
          />
        )}
      </StyledContainer>
    </StyledAside>
  )
}

export default Sidebar
